import { isEmpty, omit } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { DEFAULT_DATE_FORMAT, getUtcFormattedTime } from '../../../domain/date/date';
import { ISidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { parseClearPeriodValue, parseDateRangeValue } from '../../settlements/view/vendorBillItems/sidebar/utils';
import { EventTrackingFilterKeys, TEventTrackingFilterKey } from './EventTrackingSidebarFilters';
import { IEventTrackingPageState, IEventTrackingSidebarFilters } from './EventTrackingSidebarFiltersContext';

export const transformEventTrackingPageStateToFilters = (pageState: IEventTrackingPageState): IEventTrackingSidebarFilters => {
    const result: IEventTrackingSidebarFilters = {};
    const filterData = (pageState.filter || {}) as IEventTrackingSidebarFilters;

    // Convert period
    if (filterData.period !== undefined) {
        result.period = filterData.period;
    }
    // Convert users
    if (filterData.user_ids !== undefined) {
        result.user_ids = Array.isArray(filterData.user_ids) ? filterData.user_ids : [filterData.user_ids];
    }

    // Convert alert statuses
    if (filterData.alert_statuses !== undefined) {
        result.alert_statuses = Array.isArray(filterData.alert_statuses) ? filterData.alert_statuses : [filterData.alert_statuses];
    }

    // Convert event statuses
    if (filterData.event_statuses !== undefined) {
        result.event_statuses = Array.isArray(filterData.event_statuses) ? filterData.event_statuses : [filterData.event_statuses];
    }

    // Convert event programs
    if (filterData.event_programs !== undefined) {
        result.event_programs = Array.isArray(filterData.event_programs) ? filterData.event_programs : [filterData.event_programs];
        result.event_programs = result.event_programs.map(val => String(val));
    }

    // Convert is_opened
    if (filterData.is_opened !== undefined) {
        result.is_opened = filterData.is_opened;
    }

    // Convert participation_statuses
    if (filterData.participation_statuses !== undefined) {
        result.participation_statuses = Array.isArray(filterData.participation_statuses)
            ? filterData.participation_statuses
            : [filterData.participation_statuses];
        result.participation_statuses = result.participation_statuses.map(val => String(val));
    }

    return result;
};

export const transformEventTrackingSavedFilterValuesFromDto = (filter: ISavedFilter['filter_values']): Record<TEventTrackingFilterKey, unknown> => {
    const result: Record<TEventTrackingFilterKey, unknown> = {} as Record<TEventTrackingFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case EventTrackingFilterKeys.period:
            case EventTrackingFilterKeys.user_ids:
            case EventTrackingFilterKeys.alert_statuses:
            case EventTrackingFilterKeys.event_statuses:
            case EventTrackingFilterKeys.event_programs:
            case EventTrackingFilterKeys.is_opened:
            case EventTrackingFilterKeys.participation_statuses:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformEventTrackingSavedFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TEventTrackingFilterKey, unknown> => {
    const result: Record<TEventTrackingFilterKey, unknown> = {} as Record<TEventTrackingFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case EventTrackingFilterKeys.period:
            case EventTrackingFilterKeys.user_ids:
            case EventTrackingFilterKeys.alert_statuses:
            case EventTrackingFilterKeys.event_statuses:
            case EventTrackingFilterKeys.event_programs:
            case EventTrackingFilterKeys.is_opened:
            case EventTrackingFilterKeys.participation_statuses:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const transformEventTrackingSavedFilterToUser = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    let result: Record<TEventTrackingFilterKey, string> = {} as Record<TEventTrackingFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid them "unknown"
        if (isUndefined(value) || value === null) continue;

        switch (key) {
            case EventTrackingFilterKeys.period:
                // for custom range value we use such structure 'key::period'
                const clearPeriodValue = parseClearPeriodValue(value);

                const period =
                    context.alertTrackingPeriodOptions.find(option => {
                        return option.value === clearPeriodValue;
                    })?.label || 'unknown';

                if (clearPeriodValue !== 'custom_range') {
                    result[key] = period;
                    break;
                }

                const dateRange = parseDateRangeValue(value);

                if (!dateRange) {
                    result[key] = 'unknown';
                    break;
                }

                result[key] =
                    `${period}(${getUtcFormattedTime(dateRange.start, DEFAULT_DATE_FORMAT)}-${getUtcFormattedTime(dateRange.end, DEFAULT_DATE_FORMAT)})`;
                break;

            case EventTrackingFilterKeys.alert_statuses:
                result[key] = value
                    .map((val: string) => {
                        const option = context.alertTrackStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            case EventTrackingFilterKeys.event_statuses:
                result[key] = value
                    .map((val: string) => {
                        const option = context.eventStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            case EventTrackingFilterKeys.event_programs:
                result[key] = value
                    .map((val: string) => {
                        const option = context.alertTrackingProgramOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            case EventTrackingFilterKeys.user_ids:
                result[key] = value
                    .map((val: string) => {
                        const option = context.alertTrackingUserOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            case EventTrackingFilterKeys.is_opened:
                result[key] = context.alertTrackOpenedOptions.find(option => option.value === value)?.label || 'unknown';
                break;

            case EventTrackingFilterKeys.participation_statuses:
                result[key] = value
                    .map((val: string) => {
                        const option = context.alertTrackParticipationStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TEventTrackingFilterKey, string>;
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case EventTrackingFilterKeys.period:
                if (
                    typeof value === 'string' &&
                    !context.alertTrackingPeriodOptions.some(opt => {
                        // for custom range value we use such structure 'key::period'
                        const clearPeriod = value?.split('::')?.at(0);

                        return opt.value === clearPeriod;
                    })
                ) {
                    invalidFields.push(key);
                }
                break;

            case EventTrackingFilterKeys.alert_statuses:
                if (Array.isArray(value)) {
                    const validOptionValues = context.alertTrackStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case EventTrackingFilterKeys.event_statuses:
                if (Array.isArray(value)) {
                    const validOptionValues = context.eventStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case EventTrackingFilterKeys.user_ids:
                if (Array.isArray(value)) {
                    const validOptionValues = context.alertTrackingUserOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case EventTrackingFilterKeys.event_programs:
                if (Array.isArray(value)) {
                    const validOptionValues = context.alertTrackingProgramOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case EventTrackingFilterKeys.is_opened:
                if (typeof value === 'string') {
                    const validOptionValues = context.alertTrackOpenedOptions.map(opt => opt.value);
                    if (!validOptionValues.includes(value)) {
                        invalidFields.push(key);
                    }
                }
                break;

            case EventTrackingFilterKeys.participation_statuses:
                if (Array.isArray(value)) {
                    const validOptionValues = context.alertTrackParticipationStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            default:
                break; // Ignore other keys
        }
    }

    return invalidFields;
};

function getEmptyFilterProperties(obj: Record<TEventTrackingFilterKey, string>): TEventTrackingFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TEventTrackingFilterKey[];
}
